/**
 * Pure helpers for the generic "Generate Report" modal — used today by
 * Contacts and Exhibitions. Per-type metadata (which reports exist, what
 * each one is called, which ones support thumbnails) lives in
 * `REPORT_CATALOGS`, keyed by entity type.
 *
 * Kept separate from the React component so they can be unit-tested
 * without jsdom/MSAL setup, in line with the project's preference for
 * pure functions (see AGENTS.md).
 */

import { getConfig } from "../../../config";

export const SUPPORTED_REPORT_FORMATS = ["pdf", "xlsx"];
export const DEFAULT_REPORT_FORMAT = "pdf";

/**
 * Whether to include thumbnails in generated reports by default. Mirrors the
 * server's `DEFAULT_INCLUDE_IMAGES`. Embedding thumbnails requires one S3
 * fetch per unique artwork, so we let the user opt out from the modal when
 * generating a large report.
 */
export const DEFAULT_INCLUDE_IMAGES = true;

/**
 * Whether to print the boilerplate preamble (the left column under the
 * invoice line items) by default. Mirrors the server's
 * `DEFAULT_INCLUDE_PREAMBLE`. Both the Invoice and the Credit Note opt into
 * the toggle via `reportsWithPreamble`; when switched off the preamble is
 * replaced by the banking terms (plus the Invoice's bank account details)
 * moved up into the left column beside the totals.
 */
export const DEFAULT_INCLUDE_PREAMBLE = true;

/**
 * Whether sale prices are printed by default. Mirrors the server's
 * `DEFAULT_INCLUDE_PRICES`. Only the brochure reads it, and only the
 * artwork-driven entry point turns it off: there the instance is borrowed
 * solely for its reference and edition line, so its price would be arbitrary.
 */
export const DEFAULT_INCLUDE_PRICES = true;

/**
 * Whether the extra (French) translations are shown by default. They are off
 * by default — the Invoice and Credit Note reports expose a "Show additional
 * translations" toggle; when it is off the document prints in English only
 * (the server's `disableTranslations` flag), when on it adds the French
 * translations for every label, the preamble and the dates.
 */
export const DEFAULT_SHOW_ADDITIONAL_TRANSLATIONS = false;

/**
 * Supported page orientations + the default. Only reports listed in a
 * catalogue's `reportsWithOrientation` expose the toggle (the Artwork
 * Report today); everything else ignores it. Mirrors the server's
 * `SUPPORTED_ORIENTATIONS` / `DEFAULT_ORIENTATION`.
 */
export const SUPPORTED_REPORT_ORIENTATIONS = ["portrait", "landscape"];
// Portrait by default. The orientation toggle is dormant for now (no report
// in `reportsWithOrientation`); the plumbing is kept for future reports.
export const DEFAULT_REPORT_ORIENTATION = "portrait";

/**
 * Per-entity-type catalogue of available reports. Each catalogue carries:
 *   - `reports`        — generic Reports menu slugs, in menu order
 *   - `hiddenReports`  — supported slugs launched through dedicated generators
 *   - `reportMeta`     — { label, menuLabel, defaultFilenameSlug,
 *                          sentenceLabel } per slug
 *   - `reportsWithoutImages` — subset of `reports` that don't reference
 *                              any artwork thumbnails
 *   - `defaultReport`  — initial selection if the modal opens without one
 *   - `targetNoun`     — singular noun used in the modal target description
 *                        (e.g. "contact", "exhibition")
 *
 * Adding a new report is a one-line edit per catalogue.
 */
export const REPORT_CATALOGS = {
  Artwork: {
    targetNoun: "artwork",
    targetNounPlural: "artworks",
    defaultReport: "requestList",
    reports: ["requestList"],
    reportsWithoutImages: [],
    // Portrait-only for now — no orientation toggle.
    reportMeta: {
      requestList: {
        label: "Request List Report",
        menuLabel: "Request List Report",
        defaultFilenameSlug: "request-list-report",
        sentenceLabel: "request list report",
        formats: ["pdf", "xlsx"],
      },
    },
  },
  Contact: {
    targetNoun: "contact",
    targetNounPlural: "contacts",
    defaultReport: "sales",
    reports: [
      "sales",
      "salesHistory",
      "offers",
      "offerHistory",
      "requests",
      "topSpenders",
      "collectors",
      "collectionsList",
      "clientStrategy",
      "contactDetails",
      "clientListReport",
      "contactReport",
    ],
    reportsWithoutImages: [
      "requests",
      "topSpenders",
      "collectors",
      "clientStrategy",
      "contactDetails",
      "clientListReport",
      "contactReport",
    ],
    reportMeta: {
      sales: {
        label: "Contact Sales List",
        menuLabel: "Sales List",
        defaultFilenameSlug: "contact-sales-list",
        sentenceLabel: "sales list",
      },
      salesHistory: {
        label: "Contact Sales History",
        menuLabel: "Sales History",
        defaultFilenameSlug: "contact-sales-history",
        sentenceLabel: "sales history",
      },
      offers: {
        label: "Contact Offer List",
        menuLabel: "Offer List",
        defaultFilenameSlug: "contact-offer-list",
        sentenceLabel: "offer list",
      },
      offerHistory: {
        label: "Contact Offer History",
        menuLabel: "Offer History",
        defaultFilenameSlug: "contact-offer-history",
        sentenceLabel: "offer history",
      },
      requests: {
        label: "Contact Requests List",
        menuLabel: "Requests List",
        defaultFilenameSlug: "contact-requests-list",
        sentenceLabel: "requests list",
      },
      topSpenders: {
        label: "Contact Top Spenders",
        menuLabel: "Top Spenders",
        defaultFilenameSlug: "contact-top-spenders",
        sentenceLabel: "top spenders list",
      },
      collectors: {
        label: "Top Collector's Affiliations",
        menuLabel: "Top Collector's Affiliations",
        defaultFilenameSlug: "contact-collectors-affiliations",
        sentenceLabel: "collectors affiliations list",
      },
      collectionsList: {
        label: "Contact Collection Details",
        menuLabel: "Collection Details",
        defaultFilenameSlug: "contact-collection-details",
        sentenceLabel: "collection details",
      },
      clientStrategy: {
        label: "Client Strategy",
        menuLabel: "Client Strategy",
        defaultFilenameSlug: "client-strategy",
        sentenceLabel: "client strategy",
      },
      contactDetails: {
        label: "Contact Details",
        menuLabel: "Contact Details",
        defaultFilenameSlug: "contact-details",
        sentenceLabel: "contact details",
      },
      clientListReport: {
        label: "Client List Report",
        menuLabel: "Client List Report",
        defaultFilenameSlug: "client-list-report",
        sentenceLabel: "client list report",
      },
      contactReport: {
        label: "Contact Report",
        menuLabel: "Contact Report",
        defaultFilenameSlug: "contact-report",
        sentenceLabel: "contact report",
      },
    },
  },
  Exhibition: {
    targetNoun: "exhibition",
    targetNounPlural: "exhibitions",
    defaultReport: "artfairPriceList",
    reports: ["artfairPriceList", "exhibitionPriceList"],
    reportsWithoutImages: [],
    reportMeta: {
      artfairPriceList: {
        label: "Artfair Price List",
        menuLabel: "Artfair Price List",
        defaultFilenameSlug: "artfair-price-list",
        sentenceLabel: "artfair price list",
      },
      exhibitionPriceList: {
        label: "Exhibition Price List",
        menuLabel: "Exhibition Price List",
        defaultFilenameSlug: "exhibition-price-list",
        sentenceLabel: "exhibition price list",
      },
    },
  },
  Instance: {
    targetNoun: "instance",
    targetNounPlural: "instances",
    defaultReport: "instanceList",
    reports: [
      "instanceList",
      "instanceViewingPrice",
      "instanceListLocation",
      "instanceListImage",
      "instanceWorksByStatus",
      "instanceWorksAvailable",
      "instanceWorksOnHold",
      "instanceSummary",
      "instanceLocationCondition",
      "instanceHoldPrice",
      "instanceSales",
    ],
    // Brochures are launched by the dedicated PDF Generator action rather
    // than appearing in the generic Reports dropdown.
    hiddenReports: ["brochure"],
    // Instance List uses a JJ Ref column instead of thumbnails. The brochure
    // picks images per slot, so neither uses the generic image toggle.
    reportsWithoutImages: ["instanceList", "brochure"],
    // The brochure renders through the queued report worker (it can cover
    // hundreds of instances), so the modal queues a ReportJob instead of
    // waiting on a synchronous download.
    reportsQueued: ["brochure"],
    reportMeta: {
      instanceList: {
        label: "Instance List",
        menuLabel: "Instance List",
        defaultFilenameSlug: "instance-list",
        sentenceLabel: "instance list",
        formats: ["pdf", "xlsx"],
      },
      instanceViewingPrice: {
        label: "Instance Viewing Price List",
        menuLabel: "Instance Viewing Price List",
        defaultFilenameSlug: "instance-viewing-price-list",
        sentenceLabel: "instance viewing price list",
        formats: ["pdf", "xlsx"],
      },
      instanceListLocation: {
        label: "Instance List Location",
        menuLabel: "Instance List Location",
        defaultFilenameSlug: "instance-list-location",
        sentenceLabel: "instance list location",
        formats: ["pdf", "xlsx"],
      },
      instanceListImage: {
        label: "Instance List Image",
        menuLabel: "Instance List Image",
        defaultFilenameSlug: "instance-list-image",
        sentenceLabel: "instance list image",
        formats: ["pdf", "xlsx"],
      },
      instanceWorksByStatus: {
        label: "Instance Works by Status",
        menuLabel: "Instance Works by Status",
        defaultFilenameSlug: "instance-works-by-status",
        sentenceLabel: "instance works by status",
        formats: ["pdf", "xlsx"],
      },
      instanceWorksAvailable: {
        label: "Instance Works Available",
        menuLabel: "Instance Works Available",
        defaultFilenameSlug: "instance-works-available",
        sentenceLabel: "instance works available",
        formats: ["pdf", "xlsx"],
      },
      instanceWorksOnHold: {
        label: "Instance Works On Hold",
        menuLabel: "Instance Works On Hold",
        defaultFilenameSlug: "instance-works-on-hold",
        sentenceLabel: "instance works on hold",
        formats: ["pdf", "xlsx"],
      },
      instanceSummary: {
        label: "Instance Summary / Notes",
        menuLabel: "Instance Summary / Notes",
        defaultFilenameSlug: "instance-summary",
        sentenceLabel: "instance summary",
        formats: ["pdf", "xlsx"],
      },
      instanceLocationCondition: {
        label: "Instance Conditions List",
        menuLabel: "Instance Conditions List",
        defaultFilenameSlug: "instance-conditions-list",
        sentenceLabel: "instance conditions list",
        formats: ["pdf", "xlsx"],
      },
      instanceHoldPrice: {
        label: "Hold & Viewing Price List",
        menuLabel: "Hold & Viewing Price List",
        defaultFilenameSlug: "instance-hold-price-list",
        sentenceLabel: "hold and viewing price list",
        formats: ["pdf", "xlsx"],
      },
      instanceSales: {
        label: "Instance Sales Report",
        menuLabel: "Instance Sales Report",
        defaultFilenameSlug: "instance-sales-report",
        sentenceLabel: "instance sales report",
        formats: ["pdf", "xlsx"],
      },
      brochure: {
        label: "Instance Brochure",
        menuLabel: "Brochure",
        defaultFilenameSlug: "instance-brochure",
        sentenceLabel: "brochure",
        formats: ["pdf"],
      },
    },
  },
  Invoice: {
    targetNoun: "invoice",
    targetNounPlural: "invoices",
    defaultReport: "invoice",
    reports: [
      "invoice",
      "creditNote",
      "salesNetReport",
      "salesNetReportCreditNotes",
    ],
    reportsWithoutImages: ["salesNetReport", "salesNetReportCreditNotes"],
    // Both the Invoice and the Credit Note expose the preamble toggle (on by
    // default); when off the boilerplate preamble is replaced by the banking
    // terms (and, for the Invoice, the bank account details) moved up into the
    // left column beside the totals.
    reportsWithPreamble: ["invoice", "creditNote"],
    // Both invoice flavours can show the additional (French) translations;
    // off by default they print in English only.
    reportsWithTranslationToggle: ["invoice", "creditNote"],
    reportMeta: {
      invoice: {
        label: "Invoice",
        menuLabel: "Invoice",
        defaultFilenameSlug: "invoice",
        sentenceLabel: "invoice",
        formats: ["pdf"],
      },
      creditNote: {
        label: "Credit Note",
        menuLabel: "Credit Note",
        defaultFilenameSlug: "credit-note",
        sentenceLabel: "credit note",
        formats: ["pdf"],
      },
      salesNetReport: {
        label: "Sales Net Report (Finance)",
        menuLabel: "Sales Net Report",
        defaultFilenameSlug: "sales-net-report",
        sentenceLabel: "sales net report",
        formats: ["pdf", "xlsx"],
      },
      salesNetReportCreditNotes: {
        label: "Sales Net Report — Credit Notes",
        menuLabel: "Sales Net Report (Credit Notes)",
        defaultFilenameSlug: "sales-net-report-credit-notes",
        sentenceLabel: "sales net report (credit notes)",
        formats: ["pdf", "xlsx"],
      },
    },
  },
};

/**
 * Pure: union of every supported report slug across every catalogue.
 * Used by `buildExportPayload` (which doesn't know about types) and
 * `supportsImages` (a global function).
 */
export const SUPPORTED_REPORTS = Array.from(
  new Set(
    Object.values(REPORT_CATALOGS).flatMap(catalog => [
      ...catalog.reports,
      ...(catalog.hiddenReports || []),
    ]),
  ),
);

/**
 * Pure: union of every "no-image" report slug across every catalogue.
 * Kept as a top-level export for back-compat with the original
 * Contact-only modal.
 */
export const REPORTS_WITHOUT_IMAGES = Array.from(
  new Set(
    Object.values(REPORT_CATALOGS).flatMap(
      catalog => catalog.reportsWithoutImages || [],
    ),
  ),
);

/**
 * Pure: union of every report slug that prints a boilerplate preamble (and
 * so can toggle it on/off). Built from the catalogues' `reportsWithPreamble`.
 */
export const REPORTS_WITH_PREAMBLE = Array.from(
  new Set(
    Object.values(REPORT_CATALOGS).flatMap(
      catalog => catalog.reportsWithPreamble || [],
    ),
  ),
);

/**
 * Pure: union of every report slug that exposes the "Show additional
 * translations" toggle. Built from the catalogues'
 * `reportsWithTranslationToggle`.
 */
export const REPORTS_WITH_TRANSLATION_TOGGLE = Array.from(
  new Set(
    Object.values(REPORT_CATALOGS).flatMap(
      catalog => catalog.reportsWithTranslationToggle || [],
    ),
  ),
);

/**
 * Pure: union of every report slug that exposes the portrait/landscape
 * orientation toggle. Built from the catalogues' `reportsWithOrientation`.
 */
export const REPORTS_WITH_ORIENTATION = Array.from(
  new Set(
    Object.values(REPORT_CATALOGS).flatMap(
      catalog => catalog.reportsWithOrientation || [],
    ),
  ),
);

/**
 * Pure: union of every report slug that goes through the queued report
 * worker (`queueReportExport` mutation) instead of the synchronous
 * `/export-report` download. Built from the catalogues' `reportsQueued`.
 */
export const QUEUED_REPORTS = Array.from(
  new Set(
    Object.values(REPORT_CATALOGS).flatMap(
      catalog => catalog.reportsQueued || [],
    ),
  ),
);

/**
 * Pure: default report when no `type` (or no `initialReport`) is given.
 * Preserved as an export for back-compat — currently the Contact catalog's
 * default ("sales") because that's what the legacy menu wired in.
 */
export const DEFAULT_REPORT = REPORT_CATALOGS.Contact.defaultReport;

/**
 * Pure: report slugs available for a given type. Falls back to the
 * Contact catalogue when the type is unknown so the modal still works.
 */
export const getSupportedReportsForType = type =>
  (REPORT_CATALOGS[type] || REPORT_CATALOGS.Contact).reports;

/**
 * Pure: default report for a given type (used as the modal's initial
 * selection when no `report` prop is provided).
 */
export const getDefaultReportForType = type =>
  (REPORT_CATALOGS[type] || REPORT_CATALOGS.Contact).defaultReport;

/**
 * Pure: human-friendly metadata for a single report flavour. Searches
 * every catalogue so callers can drop the type if they have only the
 * slug.
 *
 * Falls back to the Contact "sales" entry so the modal always has
 * something to render.
 */
export const getReportMeta = report => {
  for (const catalog of Object.values(REPORT_CATALOGS)) {
    const meta = catalog.reportMeta[report];
    if (meta) return meta;
  }
  return REPORT_CATALOGS.Contact.reportMeta[
    REPORT_CATALOGS.Contact.defaultReport
  ];
};

/**
 * Pure: does the given report ever embed artwork thumbnails? Used by the
 * modal to decide whether to render the "Include images" toggle (and its
 * slow-with-many-rows warning).
 */
export const supportsImages = report =>
  !REPORTS_WITHOUT_IMAGES.includes(report);

/**
 * Pure: does the given report print a boilerplate preamble? Used by the modal
 * to decide whether to render the "Include Preamble" toggle.
 */
export const supportsPreamble = report =>
  REPORTS_WITH_PREAMBLE.includes(report);

/**
 * Pure: does the given report expose the "Show additional translations"
 * toggle? Used by the modal to decide whether to render that checkbox.
 */
export const supportsTranslationToggle = report =>
  REPORTS_WITH_TRANSLATION_TOGGLE.includes(report);

/**
 * Pure: does the given report expose the portrait/landscape orientation
 * toggle? Used by the modal to decide whether to render the radios.
 */
export const supportsOrientation = report =>
  REPORTS_WITH_ORIENTATION.includes(report);

/**
 * Pure: is the given report generated asynchronously via the queued report
 * worker? Queued reports return a ReportJob to track in the Reporting
 * section instead of a direct download.
 */
export const isQueuedReport = report => QUEUED_REPORTS.includes(report);

/**
 * Pure: true when `search` is a non-empty plain object (i.e. the user has
 * an active filter). Empty objects / null / undefined / arrays are treated
 * as "no search" and omitted from the payload so the server doesn't run an
 * unnecessary constraint pass.
 */
const hasMeaningfulSearch = search =>
  search != null &&
  typeof search === "object" &&
  !Array.isArray(search) &&
  Object.keys(search).length > 0;

/**
 * Mirror the input shape used by exportActiveReportJSON / exportEmails:
 *   - select === "all" + listId  -> { listId } (+ search when provided)
 *   - select === "all"           -> { search }
 *   - otherwise                  -> { ids }    (+ search when provided)
 *
 * `search` is now always forwarded when it carries any filter, even
 * alongside explicit `ids` or a `listId`. The server uses it to constrain
 * the per-row sub-table (offers / sales / requests) so the report
 * reflects the user's active filters and not just the cohort selection.
 *
 * Always carries `type` so the API can route to the right report, `format`
 * so the API knows whether to render PDF or XLSX, and `report` so the API
 * knows which sub-template to render.
 */
export const buildExportPayload = ({
  type,
  ids,
  artworkIds,
  exhibitionIds,
  listId,
  search,
  select,
  format = DEFAULT_REPORT_FORMAT,
  report = DEFAULT_REPORT,
  includeImages = DEFAULT_INCLUDE_IMAGES,
  includePreamble = DEFAULT_INCLUDE_PREAMBLE,
  includePrices = DEFAULT_INCLUDE_PRICES,
  showAdditionalTranslations = DEFAULT_SHOW_ADDITIONAL_TRANSLATIONS,
  orientation = DEFAULT_REPORT_ORIENTATION,
  imageSlots,
  brochurePages,
  publicCollections,
}) => {
  const resolvedReport = SUPPORTED_REPORTS.includes(report)
    ? report
    : DEFAULT_REPORT;
  const base = {
    type,
    format: SUPPORTED_REPORT_FORMATS.includes(format)
      ? format
      : DEFAULT_REPORT_FORMAT,
    report: resolvedReport,
    includeImages: includeImages !== false,
    includePreamble: includePreamble !== false,
    // Only sent when switched off; the server defaults it to true.
    ...(includePrices === false ? { includePrices: false } : {}),
    // Per-slot image selections for image-led reports (Instance brochure).
    // Only forwarded when the caller supplies a non-empty map.
    ...(imageSlots && Object.keys(imageSlots).length > 0
      ? { imageSlots }
      : {}),
    // Per-page on/off toggles for image-led reports (Instance brochure). Only
    // forwarded when the caller has actually toggled a page; an omitted map
    // tells the server to include every page.
    ...(brochurePages && Object.keys(brochurePages).length > 0
      ? { brochurePages }
      : {}),
    // Per-artist Selected Public Collections overrides (Instance brochure).
    // Only forwarded when the user edited a list.
    ...(publicCollections && Object.keys(publicCollections).length > 0
      ? { publicCollections }
      : {}),
    // The "Show additional translations" toggle is off by default, so a
    // translation-capable report prints in English unless it's switched on.
    // Only forwarded for reports that expose the toggle, keeping the common
    // payload tidy (the server defaults `disableTranslations` to false).
    ...(supportsTranslationToggle(resolvedReport) &&
    showAdditionalTranslations !== true
      ? { disableTranslations: true }
      : {}),
    // Page orientation, only forwarded for reports that expose the toggle
    // (Artwork Report). The server defaults to portrait when omitted.
    ...(supportsOrientation(resolvedReport)
      ? {
          orientation: SUPPORTED_REPORT_ORIENTATIONS.includes(orientation)
            ? orientation
            : DEFAULT_REPORT_ORIENTATION,
        }
      : {}),
  };

  // An artwork / exhibition cohort defines the selection on its own — the
  // server expands it to instances — so it takes precedence over ids, a saved
  // list or a search (which would all be in the wrong entity's terms).
  if (
    (Array.isArray(artworkIds) && artworkIds.length > 0) ||
    (Array.isArray(exhibitionIds) && exhibitionIds.length > 0)
  ) {
    return {
      ...base,
      ...(artworkIds?.length > 0 ? { artworkIds } : {}),
      ...(exhibitionIds?.length > 0 ? { exhibitionIds } : {}),
    };
  }

  if (select === "all") {
    if (listId) {
      return hasMeaningfulSearch(search)
        ? { ...base, listId, search }
        : { ...base, listId };
    }
    return { ...base, search: search || {} };
  }

  const idsPayload = { ...base, ids: ids || [] };
  return hasMeaningfulSearch(search)
    ? { ...idsPayload, search }
    : idsPayload;
};

/**
 * Derive the reporting host from REACT_APP_GRAPHQL_URI by stripping the
 * trailing `/graphql` segment and swapping the `api.` subdomain for
 * `backend.`.  The report endpoint is mounted at `/export-report` on the
 * backend host, not the GraphQL (api) host.
 */
export const getApiBaseUrl = (
  graphqlUri = getConfig(
    "REACT_APP_GRAPHQL_URI",
    "http://localhost:4000/graphql",
  ),
) => graphqlUri.replace(/\/graphql\/?$/, "").replace(/\/\/api\./, "//backend.");

/**
 * Pure: per-format presentation metadata used by the React component to
 * advertise the right Accept header, file extension and human label.
 */
export const getReportFormatMeta = format => {
  if (format === "xlsx") {
    return {
      accept:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      extension: "xlsx",
      label: "Excel",
    };
  }
  return { accept: "application/pdf", extension: "pdf", label: "PDF" };
};

/**
 * Extract the `filename` (or `filename*=UTF-8''…`) portion from a
 * Content-Disposition header. Returns null if not found.
 */
export const parseFilenameFromContentDisposition = header => {
  if (!header || typeof header !== "string") return null;

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch) return quotedMatch[1];

  const bareMatch = header.match(/filename=([^;]+)/i);
  if (bareMatch) return bareMatch[1].trim();

  return null;
};

/**
 * Trigger a browser download for a Blob without navigating the page.
 * Uses an anchor with `download` + an object URL, then revokes it.
 *
 * Side-effecty by nature, but isolated here so the React component stays thin.
 */
export const downloadBlobAsFile = ({
  blob,
  filename,
  document: doc = document,
}) => {
  const url = URL.createObjectURL(blob);
  const anchor = doc.createElement("a");
  anchor.href = url;
  anchor.download = filename || "download";
  doc.body.appendChild(anchor);
  anchor.click();
  doc.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// Fetch helpers — isolated here so the retry logic can be unit-tested without
// mounting the React component or pulling in MSAL.
// ---------------------------------------------------------------------------

/**
 * Pure: attempt to parse an error message from a non-OK fetch Response.
 * Tries JSON first (`body.errors[]` or `body.error`), then falls back to
 * a generic "Request failed (status)" string.
 */
export const readErrorMessage = async response => {
  try {
    const body = await response.json();
    if (Array.isArray(body?.errors)) return body.errors.join(", ");
    if (body?.error) return body.error;
  } catch {
    // not JSON, fall through
  }
  return `Request failed (${response.status})`;
};

/**
 * Pure: execute one `export-report` POST with the given bearer token.
 * Kept separate from the retry wrapper so tests can stub it independently.
 *
 * Accepts an optional `apiBaseUrl` override so tests don't need to hit a real
 * server.
 */
export const fetchReport = ({
  token,
  payload,
  accept,
  apiBaseUrl = getApiBaseUrl(),
}) =>
  fetch(`${apiBaseUrl}/export-report`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
      Accept: accept,
    },
    body: JSON.stringify(payload),
  });

/**
 * Pure: call `fetchReport` and, if the server returns a 401 `InvalidToken`
 * error, silently refresh the MSAL token via `requestAccessToken` and replay
 * the request exactly once.
 *
 * Returns the final Response — either the successful one, or the failure
 * response after a single retry attempt. The caller is responsible for
 * checking `response.ok`.
 *
 * Uses `response.clone()` to peek at the 401 body so the original response
 * stream remains readable for `readErrorMessage` if the retry also fails.
 *
 * `onSessionExpired` is called when `requestAccessToken` returns null (MSAL
 * could not renew even with a popup). The caller should treat this as an
 * unrecoverable session and typically reload/redirect to login. When it is
 * called the function still returns the original 401 response so the caller
 * can decide whether to show a message before reloading.
 */
export const fetchReportWithTokenRefresh = async ({
  token,
  payload,
  accept,
  requestAccessToken,
  onSessionExpired,
  apiBaseUrl = getApiBaseUrl(),
}) => {
  let response = await fetchReport({ token, payload, accept, apiBaseUrl });

  if (response.status === 401) {
    let bodyJson = {};
    try {
      bodyJson = await response.clone().json();
    } catch {
      // not JSON — leave bodyJson empty
    }

    if (bodyJson?.error === "InvalidToken") {
      const freshToken = await requestAccessToken?.();
      if (freshToken) {
        response = await fetchReport({
          token: freshToken,
          payload,
          accept,
          apiBaseUrl,
        });
      } else {
        // MSAL could not renew — notify caller so it can reload/redirect.
        onSessionExpired?.();
      }
    }
  }

  return response;
};
